// src/components/inputs/SelectField.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
}

/**
 * SelectField (dropdown)
 * - icon ซ้าย
 * - label แสดงบนปุ่ม
 * - options = [{ value, label, pillClassName? }]
 * - value = selected value
 * - onChange(value)
 */
export default function SelectField({
    icon: Icon,
    label,
    options = [],
    value,
    onChange,
    placeholder = "เลือก",
    className = "",
    buttonClassName = "",
    menuClassName = "",
    variant = "default",
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const selected = useMemo(
        () => options.find((o) => o.value === value),
        [options, value]
    );

    useEffect(() => {
        function onDocDown(e) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, []);

    const baseBtn =
        variant === "inline"
            ? [
                "h-12 w-full bg-transparent px-4",
                "flex items-center justify-between gap-3",
                "border-0 rounded-none", // ✅ ไม่มีกรอบซ้อน
                "hover:bg-transparent", // ✅ ไม่เปลี่ยนโทน
                "active:scale-[0.99] transition hover:text-[#047DED] text-[#1D1616] hover:bg-[#F5F5F5] hover:border-none",
            ].join(" ")
            : [
                "h-12 w-full rounded-lg bg-[#F5F5F5] px-4",
                "flex items-center justify-between gap-3",
                "hover:shadow-sm active:scale-[0.99] transition hover:text-[#047DED] text-[#1D1616] hover:bg-[#F5F5F5] hover:border-none",
            ].join(" ");

    return (
        <div ref={rootRef} className={classNames("relative w-full", className)}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={classNames(baseBtn, buttonClassName)}
            >
                <div className="min-w-0 flex items-center gap-3">
                    {Icon ? (
                        <span className="inline-flex items-center justify-center">
                            <Icon width={18} height={18} />
                        </span>
                    ) : null}

                    <div className="min-w-0 text-left">
                        {/* label บรรทัดบน */}
                        {/* <div className="text-[12px] font-medium text-slate-500 leading-4">
                            {label}
                        </div> */}

                        {/* value บรรทัดล่าง */}
                        <div className="truncate text-sm font-medium">
                            {selected ? selected.label : (
                                <span>{placeholder}</span>
                            )}
                        </div>
                    </div>
                </div>

                <ChevronDown
                    size={18}
                    className={classNames(
                        "transition",
                        open && "rotate-180"
                    )}
                />
            </button>

            {open && (
                <div
                    className={classNames(
                        "absolute left-0 top-[calc(100%+8px)] z-[60] w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl",
                        menuClassName
                    )}
                >
                    <div className="max-h-[280px] overflow-auto p-2">
                        {options.map((opt) => {
                            const active = opt.value === value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        onChange?.(opt.value);
                                        setOpen(false);
                                    }}
                                    className={classNames(
                                        "w-full rounded-xl px-3 py-2",
                                        "flex items-center justify-between gap-3",
                                        "hover:bg-slate-50 active:scale-[0.99] transition",
                                        active && "bg-slate-50"
                                    )}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900">
                                            {opt.label}
                                        </div>

                                        {/* optional pill (เช่นสถานะ) */}
                                        {opt.pillClassName ? (
                                            <span
                                                className={classNames(
                                                    "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-extrabold",
                                                    opt.pillClassName
                                                )}
                                            >
                                                {opt.label}
                                            </span>
                                        ) : null}
                                    </div>

                                    {active ? (
                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-white">
                                            <Check size={16} />
                                        </span>
                                    ) : (
                                        <span className="h-7 w-7" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* clear */}
                    <div className="border-t border-slate-200 p-2">
                        <button
                            type="button"
                            onClick={() => {
                                onChange?.("");
                                setOpen(false);
                            }}
                            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            ล้างค่า
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
